import React, { useRef, useEffect, useState, useCallback } from "react";
import ReactDOM from "react-dom";
import { supabase } from "../../supabaseClient";
import "./VoiceTranscriptPanel.css";

const BACKEND_URL = process.env.REACT_APP_ZOOMEY_API_URL || 'http://localhost:3000';

function VoiceTranscriptPanel({
  isActive,
  conversationHistory,
  sessionDuration,
  userId
}) {
  const transcriptEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const listContainerRef = useRef(null);
  const [isUserScrolling, setIsUserScrolling] = useState(false);
  const scrollTimeoutRef = useRef(null);

  // Sub-tab state: "list" or "transcript" - default to "list"
  const [activeSubTab, setActiveSubTab] = useState("list");

  // Auto-switch to transcript tab when voice becomes active
  useEffect(() => {
    if (isActive) {
      setActiveSubTab("transcript");
    }
  }, [isActive]);

  // Voice sessions list state
  const [voiceSessions, setVoiceSessions] = useState([]);
  const [isLoadingSessions, setIsLoadingSessions] = useState(false);
  const [sessionsError, setSessionsError] = useState(null);

  // Search state
  const [searchQuery, setSearchQuery] = useState("");

  // Selected session state
  const [selectedSessionId, setSelectedSessionId] = useState(null);
  const [selectedSessionMessages, setSelectedSessionMessages] = useState([]);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);

  // Multi-select state
  const [isSelectMode, setIsSelectMode] = useState(false);
  const [selectedSessions, setSelectedSessions] = useState(new Set());

  // Rename modal state
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [renameSessionId, setRenameSessionId] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [isRenaming, setIsRenaming] = useState(false);

  // Delete confirmation state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteSessionId, setDeleteSessionId] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Context menu state
  const [contextMenu, setContextMenu] = useState({ show: false, x: 0, y: 0, sessionId: null });

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const ITEMS_PER_PAGE = 10;

  // Fetch voice sessions from API with pagination
  const fetchVoiceSessions = useCallback(async (page = currentPage) => {
    if (!userId) return;

    setIsLoadingSessions(true);
    setSessionsError(null);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        setSessionsError("Not authenticated");
        return;
      }

      const offset = (page - 1) * ITEMS_PER_PAGE;
      const searchParam = searchQuery ? `&search=${encodeURIComponent(searchQuery)}` : '';
      const response = await fetch(`${BACKEND_URL}/api/voice-chat?limit=${ITEMS_PER_PAGE}&offset=${offset}${searchParam}`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch voice sessions');
      }

      const data = await response.json();
      setVoiceSessions(data.sessions || []);
      setTotalCount(data.total || data.sessions?.length || 0);
      setTotalPages(Math.ceil((data.total || data.sessions?.length || 0) / ITEMS_PER_PAGE));
    } catch (error) {
      console.error("[VoiceTranscriptPanel] Error fetching sessions:", error);
      setSessionsError(error.message);
    } finally {
      setIsLoadingSessions(false);
    }
  }, [userId, searchQuery, currentPage, ITEMS_PER_PAGE]);

  // Fetch messages for a specific session
  const fetchSessionMessages = useCallback(async (sessionId) => {
    if (!sessionId) return;

    setIsLoadingMessages(true);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) return;

      const response = await fetch(`${BACKEND_URL}/api/voice-chat/${sessionId}`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch session messages');
      }

      const data = await response.json();
      setSelectedSessionMessages(data.messages || []);
    } catch (error) {
      console.error("[VoiceTranscriptPanel] Error fetching messages:", error);
      setSelectedSessionMessages([]);
    } finally {
      setIsLoadingMessages(false);
    }
  }, []);

  // Rename session
  const handleRenameSession = async () => {
    if (!renameSessionId || !renameValue.trim()) return;

    setIsRenaming(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) return;

      const response = await fetch(`${BACKEND_URL}/api/voice-chat/${renameSessionId}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ title: renameValue.trim() })
      });

      if (!response.ok) {
        throw new Error('Failed to rename session');
      }

      // Update local state
      setVoiceSessions(prev => prev.map(s =>
        s.id === renameSessionId ? { ...s, title: renameValue.trim() } : s
      ));
      setShowRenameModal(false);
      setRenameSessionId(null);
      setRenameValue("");
    } catch (error) {
      console.error("[VoiceTranscriptPanel] Error renaming session:", error);
      alert("Failed to rename session");
    } finally {
      setIsRenaming(false);
    }
  };

  // Delete single session
  const handleDeleteSession = async (sessionId) => {
    setIsDeleting(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) return;

      const response = await fetch(`${BACKEND_URL}/api/voice-chat/${sessionId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to delete session');
      }

      // Update local state
      setVoiceSessions(prev => prev.filter(s => s.id !== sessionId));
      if (selectedSessionId === sessionId) {
        setSelectedSessionId(null);
        setSelectedSessionMessages([]);
      }
      setShowDeleteConfirm(false);
      setDeleteSessionId(null);
    } catch (error) {
      console.error("[VoiceTranscriptPanel] Error deleting session:", error);
      alert("Failed to delete session");
    } finally {
      setIsDeleting(false);
    }
  };

  // Delete multiple sessions
  const handleDeleteSelected = async () => {
    if (selectedSessions.size === 0) return;

    setIsDeleting(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) return;

      // Delete all selected sessions
      const deletePromises = Array.from(selectedSessions).map(sessionId =>
        fetch(`${BACKEND_URL}/api/voice-chat/${sessionId}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json'
          }
        })
      );

      await Promise.all(deletePromises);

      // Update local state
      setVoiceSessions(prev => prev.filter(s => !selectedSessions.has(s.id)));
      if (selectedSessions.has(selectedSessionId)) {
        setSelectedSessionId(null);
        setSelectedSessionMessages([]);
      }
      setSelectedSessions(new Set());
      setIsSelectMode(false);
      setShowDeleteConfirm(false);
    } catch (error) {
      console.error("[VoiceTranscriptPanel] Error deleting sessions:", error);
      alert("Failed to delete some sessions");
    } finally {
      setIsDeleting(false);
    }
  };

  // Toggle session selection
  const toggleSessionSelection = (sessionId) => {
    setSelectedSessions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(sessionId)) {
        newSet.delete(sessionId);
      } else {
        newSet.add(sessionId);
      }
      return newSet;
    });
  };

  // Select all sessions
  const selectAllSessions = () => {
    setSelectedSessions(new Set(voiceSessions.map(s => s.id)));
  };

  // Clear selection
  const clearSelection = () => {
    setSelectedSessions(new Set());
    setIsSelectMode(false);
  };

  // Open rename modal
  const openRenameModal = (session) => {
    setRenameSessionId(session.id);
    setRenameValue(session.title || "");
    setShowRenameModal(true);
    setContextMenu({ show: false, x: 0, y: 0, sessionId: null });
  };

  // Open delete confirmation
  const openDeleteConfirm = (sessionId) => {
    setDeleteSessionId(sessionId);
    setShowDeleteConfirm(true);
    setContextMenu({ show: false, x: 0, y: 0, sessionId: null });
  };

  // Handle right-click context menu
  const handleContextMenu = (e, sessionId) => {
    e.preventDefault();
    setContextMenu({
      show: true,
      x: e.clientX,
      y: e.clientY,
      sessionId
    });
  };

  // Close context menu when clicking outside
  useEffect(() => {
    const handleClick = () => setContextMenu({ show: false, x: 0, y: 0, sessionId: null });
    if (contextMenu.show) {
      document.addEventListener('click', handleClick);
      return () => document.removeEventListener('click', handleClick);
    }
  }, [contextMenu.show]);

  // Pagination handlers
  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchVoiceSessions(page);
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      handlePageChange(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      handlePageChange(currentPage + 1);
    }
  };

  // Load sessions when List tab is active
  useEffect(() => {
    if (activeSubTab === "list") {
      fetchVoiceSessions(currentPage);
    }
  }, [activeSubTab]);

  // Load messages when a session is selected
  useEffect(() => {
    if (selectedSessionId) {
      fetchSessionMessages(selectedSessionId);
    }
  }, [selectedSessionId, fetchSessionMessages]);

  // Debounced search - reset to page 1
  useEffect(() => {
    const timer = setTimeout(() => {
      if (activeSubTab === "list") {
        setCurrentPage(1);
        fetchVoiceSessions(1);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Auto-scroll to latest message (only if user is not manually scrolling)
  useEffect(() => {
    if (!isUserScrolling && transcriptEndRef.current && activeSubTab === "transcript") {
      transcriptEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [conversationHistory, selectedSessionMessages, isUserScrolling, activeSubTab]);

  // Handle user scroll - detect if user is scrolling up
  const handleScroll = () => {
    if (!messagesContainerRef.current) return;

    const { scrollTop, scrollHeight, clientHeight } = messagesContainerRef.current;
    const isAtBottom = scrollHeight - scrollTop - clientHeight < 50;

    if (!isAtBottom) {
      setIsUserScrolling(true);

      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }

      scrollTimeoutRef.current = setTimeout(() => {
        setIsUserScrolling(false);
      }, 3000);
    } else {
      setIsUserScrolling(false);
    }
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);

  // Handle session selection from list
  const handleSelectSession = (sessionId) => {
    if (isSelectMode) {
      toggleSessionSelection(sessionId);
    } else {
      setSelectedSessionId(sessionId);
      setActiveSubTab("transcript");
    }
  };

  // Clear selection to show live transcript
  const handleShowLiveTranscript = () => {
    setSelectedSessionId(null);
    setSelectedSessionMessages([]);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString([], {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '--:--';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Determine which messages to show
  const displayMessages = selectedSessionId ? selectedSessionMessages : conversationHistory;
  const isShowingLive = !selectedSessionId && isActive;
  const isShowingHistory = selectedSessionId !== null;

  return (
    <div className="voice-transcript-panel">
      {/* Sub-tabs matching notification tab style */}
      <div className="px-3 py-2 border-bottom">
        <ul className="nav nav-item-tab-child nav-pills mb-2">
          <li className="nav-item">
            <button
              className={`nav-link ${activeSubTab === "list" ? "active" : ""}`}
              onClick={() => setActiveSubTab("list")}
            >
              <i className="bi bi-list-ul me-1"></i> List
            </button>
          </li>
          <li className="nav-item">
            <button
              className={`nav-link ${activeSubTab === "transcript" ? "active" : ""}`}
              onClick={() => setActiveSubTab("transcript")}
            >
              <i className="bi bi-chat-text me-1"></i> Transcript
              {isActive && <span className="voice-tab-indicator ms-1"></span>}
            </button>
          </li>
        </ul>
      </div>

      {/* List Tab Content */}
      {activeSubTab === "list" && (
        <div className="voice-tab-panel">
          {/* Search Bar and Actions */}
          <div className="px-3 py-1 border-bottom">
            {/* Search input matching notification style */}
            <div style={{ display: "flex", alignItems: "center" }}>
              <input
                type="text"
                className="form-control mb-2 mt-2 search-input-box"
                placeholder="Search voice sessions..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{ width: "100%", marginRight: "8px" }}
              />
              {!isSelectMode ? (
                <button
                  className="btn btn-link mb-2 mt-2 text-end card-clear-all"
                  style={{ paddingRight: "10px 14px 10px 14px" }}
                  onClick={() => setIsSelectMode(true)}
                  disabled={voiceSessions.length === 0}
                >
                  Select
                </button>
              ) : (
                <button
                  className="btn btn-link mb-2 mt-2 text-end card-clear-all"
                  style={{ paddingRight: "10px 14px 10px 14px" }}
                  onClick={clearSelection}
                >
                  Cancel
                </button>
              )}
            </div>

            {/* Select Mode Actions */}
            {isSelectMode && (
              <div className="d-flex justify-content-between align-items-center mb-2">
                <button
                  className="btn btn-sm btn-link p-0"
                  onClick={selectAllSessions}
                >
                  <i className="bi bi-check-all me-1"></i> Select All
                </button>

                {selectedSessions.size > 0 && (
                  <button
                    className="btn btn-sm btn-danger"
                    onClick={() => setShowDeleteConfirm(true)}
                  >
                    <i className="bi bi-trash me-1"></i> Delete ({selectedSessions.size})
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Voice Sessions List */}
          <div
            ref={listContainerRef}
            className="list-group notification-list p-3 custom-scrollbar voice-list-container"
          >
            {isLoadingSessions ? (
              <div className="wave-loader-container">
                <div className="wave-loader">
                  <div className="wave-bar"></div>
                  <div className="wave-bar"></div>
                  <div className="wave-bar"></div>
                  <div className="wave-bar"></div>
                  <div className="wave-bar"></div>
                </div>
                <span className="wave-loader-text">Loading voice sessions...</span>
              </div>
            ) : sessionsError ? (
              <div className="voice-error-state">
                <i className="bi bi-exclamation-circle"></i>
                <p>{sessionsError}</p>
                <button className="btn btn-sm btn-primary" onClick={fetchVoiceSessions}>
                  Retry
                </button>
              </div>
            ) : (
              <>
                {/* Show Live Session option if voice is active */}
                {isActive && (
                  <div
                    className={`list-group-item list-group-item-action right-sidebar-card mb-3 rounded border-1 p-3 voice-card-live ${!selectedSessionId ? 'active' : ''}`}
                    style={{
                      backgroundColor: "var(--input-box-bg) !important",
                      borderColor: "var(--teal-bg) !important",
                    }}
                    onClick={handleShowLiveTranscript}
                  >
                    <div className="d-flex w-100 justify-content-between mb-1">
                      <label
                        className="mb-1"
                        style={{
                          paddingTop: "4px",
                          fontSize: "var(--font-size)",
                          fontWeight: "var(--font-weight)",
                        }}
                      >
                        <i className="bi bi-mic-fill me-2" style={{ color: "var(--teal-bg)" }}></i>
                        Live Session
                      </label>
                      <div className="d-flex align-items-center">
                        <span className="badge bg-success voice-live-badge">LIVE</span>
                      </div>
                    </div>
                    <p className="mb-0 card-text text-muted" style={{ fontSize: "0.8rem" }}>
                      <i className="bi bi-clock me-1"></i>
                      {formatTime(sessionDuration)}
                    </p>
                  </div>
                )}

                {/* Empty state */}
                {voiceSessions.length === 0 && !isActive ? (
                  <div className="voice-empty-state">
                    <i className="bi bi-mic"></i>
                    <p>No voice sessions yet</p>
                    <small>Start a voice conversation using the Z button</small>
                  </div>
                ) : (
                  /* Voice Sessions List */
                  voiceSessions.map((session) => (
                    <div
                      key={session.id}
                      className={`list-group-item list-group-item-action right-sidebar-card mb-3 rounded border-1 p-3 ${selectedSessions.has(session.id) ? 'voice-card-selected' : ''}`}
                      style={{
                        backgroundColor: "var(--input-box-bg) !important",
                        borderColor: selectedSessionId === session.id ? "var(--orange-bg) !important" : "var(--border-color) !important",
                      }}
                      onClick={() => handleSelectSession(session.id)}
                      onContextMenu={(e) => handleContextMenu(e, session.id)}
                    >
                      <div className="d-flex w-100 justify-content-between mb-1">
                        <div className="d-flex align-items-center flex-grow-1 min-width-0">
                          {/* Checkbox for select mode */}
                          {isSelectMode && (
                            <div className="form-check me-2" onClick={(e) => e.stopPropagation()}>
                              <input
                                type="checkbox"
                                className="form-check-input"
                                checked={selectedSessions.has(session.id)}
                                onChange={() => toggleSessionSelection(session.id)}
                              />
                            </div>
                          )}
                          <label
                            className="mb-0 text-truncate"
                            style={{
                              fontSize: "var(--font-size)",
                              fontWeight: "var(--font-weight)",
                              cursor: "pointer",
                            }}
                          >
                            <i className="bi bi-mic me-2" style={{ opacity: 0.6 }}></i>
                            {session.title}
                          </label>
                        </div>
                        <div className="d-flex align-items-center" onClick={(e) => e.stopPropagation()}>
                          {/* Action buttons */}
                          {!isSelectMode && (
                            <>
                              <button
                                className="btn btn-link p-0 me-1"
                                onClick={() => openRenameModal(session)}
                                title="Rename"
                              >
                                <i className="bi bi-pencil"></i>
                              </button>
                              <button
                                className="btn btn-link p-0"
                                onClick={() => openDeleteConfirm(session.id)}
                                title="Delete"
                              >
                                <i className="bi bi-x" style={{ fontSize: "1.5em" }}></i>
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                      <p className="mb-0 card-text text-muted" style={{ fontSize: "0.8rem" }}>
                        <span className="me-3">
                          <i className="bi bi-calendar3 me-1"></i>
                          {formatDate(session.started_at)}
                        </span>
                        <span className="me-3">
                          <i className="bi bi-clock me-1"></i>
                          {formatDuration(session.duration_seconds)}
                        </span>
                        <span>
                          <i className="bi bi-chat-dots me-1"></i>
                          {session.message_count} msgs
                        </span>
                      </p>
                    </div>
                  ))
                )}
              </>
            )}
          </div>

          {/* Pagination Controls - Always show */}
          {!isLoadingSessions && voiceSessions.length > 0 && (
            <div className="px-3 py-2 border-top d-flex justify-content-between align-items-center voice-pagination">
              <small className="text-muted">
                {totalCount > 0 ? (
                  <>Showing {((currentPage - 1) * ITEMS_PER_PAGE) + 1}-{Math.min(currentPage * ITEMS_PER_PAGE, totalCount)} of {totalCount}</>
                ) : (
                  <>{voiceSessions.length} session{voiceSessions.length !== 1 ? 's' : ''}</>
                )}
              </small>
              {totalPages > 1 && (
                <nav>
                  <ul className="pagination pagination-sm mb-0">
                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                      <button
                        className="page-link"
                        onClick={handlePrevPage}
                        disabled={currentPage === 1}
                      >
                        <i className="bi bi-chevron-left"></i>
                      </button>
                    </li>
                    {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }
                      return (
                        <li
                          key={pageNum}
                          className={`page-item ${currentPage === pageNum ? 'active' : ''}`}
                        >
                          <button
                            className="page-link"
                            onClick={() => handlePageChange(pageNum)}
                          >
                            {pageNum}
                          </button>
                        </li>
                      );
                    })}
                    <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                      <button
                        className="page-link"
                        onClick={handleNextPage}
                        disabled={currentPage === totalPages}
                      >
                        <i className="bi bi-chevron-right"></i>
                      </button>
                    </li>
                  </ul>
                </nav>
              )}
            </div>
          )}
        </div>
      )}

      {/* Transcript Tab Content */}
      {activeSubTab === "transcript" && (
        <>
          {/* Header */}
          <div className="px-3 py-2 border-bottom d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center gap-2">
              {isShowingLive && <span className="voice-live-indicator"></span>}
              <span className="fw-semibold small">
                {isShowingLive ? 'Live Transcript' : isShowingHistory ? 'Session Transcript' : 'Transcript'}
              </span>
            </div>
            <div className="d-flex align-items-center gap-2">
              {isShowingLive && (
                <span className="badge bg-light text-dark">
                  <i className="bi bi-clock me-1"></i>
                  {formatTime(sessionDuration)}
                </span>
              )}
              {isShowingHistory && (
                <button
                  className="btn btn-sm btn-outline-primary voice-back-button"
                  // style={{color:"var(--teal-bg)", borderColor:"var(--teal-bg)"}}
                  onClick={handleShowLiveTranscript}
                  title="Back to live transcript"
                >
                  <i className="bi bi-arrow-left me-1"></i>
                  {isActive ? 'Live' : 'Back'}
                </button>
              )}
            </div>
          </div>

          {/* Transcript Messages */}
          <div
            ref={messagesContainerRef}
            className="list-group notification-list p-3 custom-scrollbar"
            style={{ overflowY: "auto", maxHeight: "69.5vh" }}
            onScroll={handleScroll}
          >
            {isLoadingMessages ? (
              <div className="wave-loader-container">
                <div className="wave-loader">
                  <div className="wave-bar"></div>
                  <div className="wave-bar"></div>
                  <div className="wave-bar"></div>
                  <div className="wave-bar"></div>
                  <div className="wave-bar"></div>
                </div>
                <span className="wave-loader-text">Loading transcript...</span>
              </div>
            ) : !displayMessages || displayMessages.length === 0 ? (
              <div className="voice-empty-state">
                <i className="bi bi-mic-mute"></i>
                <p>No transcript yet</p>
                <small>Start a voice session using the Z button</small>
              </div>
            ) : (
              <>
                {displayMessages.map((msg, index) => (
                  <div
                    key={msg.id || index}
                    className={`voice-transcript-message ${msg.role}`}
                  >
                    <div className="voice-message-header">
                      <span className="voice-message-role">
                        {msg.role === 'user' ? (
                          <><i className="bi bi-person-fill me-1"></i> YOU</>
                        ) : (
                          <><span className="zoomey-icon me-1">Z</span> ZOOMEY</>
                        )}
                      </span>
                      <span className="voice-message-time">
                        {new Date(msg.timestamp || Date.now()).toLocaleTimeString([], {
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </span>
                    </div>
                    <p className="voice-message-text mb-0">{msg.content || msg.text || ''}</p>
                  </div>
                ))}
                <div ref={transcriptEndRef} />
              </>
            )}
          </div>
        </>
      )}

      {/* Context Menu */}
      {contextMenu.show && (
        <div
          className="voice-context-menu"
          style={{ top: contextMenu.y, left: contextMenu.x }}
        >
          <button
            className="voice-context-item"
            onClick={() => {
              const session = voiceSessions.find(s => s.id === contextMenu.sessionId);
              if (session) openRenameModal(session);
            }}
          >
            <i className="bi bi-pencil me-2"></i> Rename
          </button>
          <button
            className="voice-context-item text-danger"
            onClick={() => openDeleteConfirm(contextMenu.sessionId)}
          >
            <i className="bi bi-trash me-2"></i> Delete
          </button>
        </div>
      )}

      {/* Rename Modal - Portal to body */}
      {showRenameModal && ReactDOM.createPortal(
        <div className="voice-modal-overlay">
          <div className="voice-modal">
            <div className="voice-modal-header">
              <span className="voice-modal-title">Rename Voice Session</span>
              <button
                className="voice-modal-close"
                onClick={() => setShowRenameModal(false)}
              >
                <i className="bi bi-x-lg"></i>
              </button>
            </div>
            <div className="voice-modal-body">
              <label className="voice-modal-label">Session Name</label>
              <input
                type="text"
                className="voice-modal-input"
                placeholder="Enter session name"
                value={renameValue}
                onChange={(e) => setRenameValue(e.target.value)}
                autoFocus
                onKeyPress={(e) => e.key === 'Enter' && handleRenameSession()}
              />
            </div>
            <div className="voice-modal-footer">
              <button
                className="voice-modal-btn voice-modal-btn-cancel"
                onClick={() => setShowRenameModal(false)}
              >
                Cancel
              </button>
              <button
                className="voice-modal-btn voice-modal-btn-primary"
                onClick={handleRenameSession}
                disabled={isRenaming || !renameValue.trim()}
              >
                {isRenaming ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Delete Confirmation Modal - Portal to body */}
      {showDeleteConfirm && ReactDOM.createPortal(
        <div className="voice-modal-overlay">
          <div className="voice-modal">
            <div className="voice-modal-header voice-modal-header-danger">
              <span className="voice-modal-title">Delete Voice Session</span>
              <button
                className="voice-modal-close"
                onClick={() => setShowDeleteConfirm(false)}
              >
                <i className="bi bi-x-lg"></i>
              </button>
            </div>
            <div className="voice-modal-body">
              <p className="voice-modal-text">
                {deleteSessionId
                  ? "Are you sure you want to delete this voice session? This action cannot be undone."
                  : `Are you sure you want to delete ${selectedSessions.size} voice session(s)? This action cannot be undone.`
                }
              </p>
            </div>
            <div className="voice-modal-footer">
              <button
                className="voice-modal-btn voice-modal-btn-cancel"
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setDeleteSessionId(null);
                }}
              >
                Cancel
              </button>
              <button
                className="voice-modal-btn voice-modal-btn-danger"
                onClick={() => {
                  if (deleteSessionId) {
                    handleDeleteSession(deleteSessionId);
                  } else {
                    handleDeleteSelected();
                  }
                }}
                disabled={isDeleting}
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

export default VoiceTranscriptPanel;
